// assets/AllCourses/Exercises/WordFormationExercise.jsx

import React, { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function WordFormationExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordFormationQuestions)
    ? exercise.wordFormationQuestions.map((q) => ({
        ...q,
        type: "multi",
      }))
    : [];

  /* ================= SAFE EXTERNAL ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer === "object" ? userAnswer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer } = useExerciseEngine({
    questions,
    showResult: showResults,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC WITH PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;
  if (!Object.keys(answers).length) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("wordFormationQuestions", next);

    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Base Word ${qIdx + 1}`}
              value={q.baseWord}
              onChange={(v) => {
                const next = [...questions];
                next[qIdx].baseWord = v;
                updateQuestions(next);
              }}
            />

            {/* OPTIONS */}
            <div className="space-y-3">
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3">
                  <StringField
                    value={opt}
                    onChange={(v) => {
                      const next = [...questions];
                      next[qIdx].options[optIdx] = v;
                      updateQuestions(next);
                    }}
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      const next = [...questions];
                      next[qIdx].options.splice(optIdx, 1);
                      updateQuestions(next);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const next = [...questions];
                  next[qIdx].options.push("");
                  updateQuestions(next);
                }}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answers (comma separated)"
              value={(q.correctAnswers || []).join(", ")}
              onChange={(v) => {
                const next = [...questions];
                next[qIdx].correctAnswers = v
                  .split(",")
                  .map((s) => s.trim());
                updateQuestions(next);
              }}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) => {
                const next = [...questions];
                next[qIdx].requireCapitalization = c;
                updateQuestions(next);
              }}
            />
          </Card>
        ))}

        <Button
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                baseWord: "",
                options: [],
                correctAnswers: [],
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Word Formation</CardTitle>
        <CardDescription>
          Select correct forms of the word
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = Array.isArray(answers[qIdx])
            ? answers[qIdx]
            : [];

          return (
            <div
              key={qIdx}
              className="p-6 rounded-xl border bg-muted/40 space-y-4"
            >
              <p className="text-xl font-bold text-blue-700">
                {q.baseWord}
              </p>

              {q.options.map((opt, optIdx) => {
                const isSelected = selected.includes(opt);

                const isCorrect = q.correctAnswers?.includes(opt);

                return (
                  <div
                    key={optIdx}
                    onClick={() => {
                      if (showResults) return;

                      const updated = isSelected
                        ? selected.filter((o) => o !== opt)
                        : [...selected, opt];

                      setAnswer(qIdx, updated);
                    }}
                    className={cn(
                      "flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition",

                      isSelected && !showResults && "bg-accent",

                      showResults &&
                        isSelected &&
                        isCorrect &&
                        "bg-green-100 border-green-400",

                      showResults &&
                        isSelected &&
                        !isCorrect &&
                        "bg-red-100 border-red-400"
                    )}
                  >
                    <Checkbox checked={isSelected} />
                    <Label className="flex-1 cursor-pointer">
                      {opt}
                    </Label>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}